import type {
  ContractAiRevisionRunSummary,
  ContractDocument,
  ContractVersionView,
} from "@closer/shared";
import { CONTRACT_AI_REVISION_FACT_LOSS_WARNING } from "@closer/shared";
import { Badge, Button, Card, FormField, InfoBanner, Select, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { AiAdvisoryNotice } from "../../components/AiOutputNotice";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * AI REVİZYON ÖNERİSİ PANELİ (CLOS-207 `[190]` · CLOS-1007).
 *
 * ### ⚠ ÜRETİLEN METİN OTOMATİK UYGULANMIYOR — hatta KAYDEDİLMİYOR bile
 *
 * Kart: *"Geçici AI revizyonu kanonik revizyon kaydı değildir; kullanıcı açıkça 'revizyon
 * önerisi olarak kaydet' command'ı vermedikçe CLOS-138 çalışmaz."* Bu yüzden ekranda iki
 * ayrı düğme var ve ikincisi ayrı bir uca gidiyor. Üretilen metni doğrudan manuel öneri
 * formuna yapıştırsaydık, kaydın AI kökeni (hangi model, hangi yönerge sürümü) kaybolurdu.
 *
 * ### ⚠ MEVCUT ve ÖNERİLEN METİN YAN YANA
 *
 * Kart: *"Mevcut metin, önerilen metin, gerekçe/amaç/varsayım ve kaynak aralığı yan yana
 * veya responsive kart görünümünde sunulur."* Mobilde alt alta (`sm:grid-cols-2`), çünkü
 * dar ekranda iki sütun her ikisini de okunmaz yapar.
 *
 * ### ⚠ VARSAYIM ve EKSİK BAĞLAM AYRI GÖSTERİLİYOR
 *
 * Model bir bedel/tarih uyduramaz; ya varsayımını yazar ya da eksik bağlamı söyler. İkisi
 * ekranda ayrı ayrı duruyor — tek bir "notlar" listesinde birleştirseydik, kullanıcının
 * DOLDURMASI gereken bir boşluk ile modelin YAPTIĞI bir kabul aynı görünürdü.
 */
export function ContractAiRevisionPanel({ contractId }: { contractId: string }) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const [acik, setAcik] = useState(false);
  const [versiyon, setVersiyon] = useState("");
  const [hedef, setHedef] = useState("");
  const [talimat, setTalimat] = useState("");
  const [calisma, setCalisma] = useState<ContractAiRevisionRunSummary | null>(null);

  /**
   * ⚠ VERSİYON LİSTESİ BURADA ÇEKİLİYOR, sayfadan prop olarak ALINMIYOR: sayfadaki liste
   * manuel öneri PENCERESİ açıkken çekiliyor (`enabled: oneriAcik`) ve bu panel açıkken
   * boş kalıyordu — panelin kaynak versiyon listesi hep boş görünürdü. Ölçüldü: E2E'de
   * açılır liste yalnız "Seçin" seçeneğiyle geldi.
   */
  const versiyonSorgusu = useQuery({
    enabled: acik,
    queryKey: tenantKey(tenantId, "contract", contractId, "versiyonlar", "ai-oneri"),
    queryFn: async () =>
      (
        await api<ContractVersionView[]>(`/api/contracts/${contractId}/versions?limit=50`, {
          tenantId,
        })
      ).data,
  });

  const belge = useQuery({
    enabled: acik && versiyon !== "",
    queryKey: tenantKey(tenantId, "contract", contractId, "versiyon-belgesi", versiyon),
    queryFn: async () =>
      (
        await api<{ document: ContractDocument | null }>(
          `/api/contracts/${contractId}/versions/${versiyon}/document`,
          { tenantId },
        )
      ).data,
  });

  const secenekler = hedefSecenekleri(belge.data?.document ?? null);
  const secili = secenekler.find((h) => h.deger === hedef) ?? null;

  /** ⚠ Versiyon değişince hedef geçersiz: eski maddeye ait seçim yeni belgede yok. */
  useEffect(() => {
    setHedef("");
    setCalisma(null);
  }, [versiyon]);

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const uret = useMutation({
    mutationFn: async () => {
      const [maddeId = "", parca = ""] = (secili?.deger ?? "").split("#");
      return (
        await api<ContractAiRevisionRunSummary>(`/api/contracts/${contractId}/ai-revisions`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({
            sourceVersionId: versiyon,
            targetClauseId: maddeId,
            targetBlockIndex: parca === "baslik" ? null : Number(parca),
            currentText: secili?.metin ?? "",
            instruction: talimat.trim(),
          }),
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data;
    },
    onSuccess: async (sonuc) => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "subscription-overview"),
      });
      setCalisma(sonuc);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Öneri üretilemedi"),
  });

  const kaydet = useMutation({
    mutationFn: async (runId: string) =>
      (
        await api<ContractAiRevisionRunSummary>(
          `/api/contracts/${contractId}/ai-revisions/${runId}/save`,
          { method: "POST", tenantId, body: JSON.stringify({}) },
        )
      ).data,
    onSuccess: async (sonuc) => {
      /** ⚠ Kanonik revizyon listesi tazeleniyor: yeni öneri orada doğdu. */
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", contractId, "revizyonlar"),
      });
      setCalisma(sonuc);
      toast.success("Revizyon önerisi kaydedildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Öneri kaydedilemedi"),
  });

  /** ⚠ Sunucu kapısı `contract.revision`; ekran onu önden söylüyor. */
  if (!can("contract.revision")) return null;

  /**
   * ⚠ Yalnız uygulama içi metin taşıyan versiyonlar: dosya versiyonunun maddesi yok.
   *
   * ⚠ ÖLÇÜT `sourceType` DEĞİL (BUG-020 · Mega E2E'de ölçüldü). Süzgeç
   * `sourceType === "editor"` idi ve ŞABLONDAN doğan sözleşmelerde panel hiç
   * kullanılamıyordu — kaynak versiyon listesi boş kalıyordu. Kanonik küme beş değer
   * taşıyor (`template`, `version_copy` de metin taşıyabiliyor); ama listeyi bu türlere
   * genişletmek de yanlış olurdu, çünkü şablon versiyonu DOSYA da olabiliyor
   * (`fromTemplate.ts`: `document: storageObjectId ? null : belge`).
   *
   * Gerçeği yalnız satırın kendisi biliyor; sunucu artık `hasDocument` ile söylüyor.
   */
  const metinliVersiyonlar = (versiyonSorgusu.data ?? []).filter((v) => v.hasDocument);

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">AI ile revizyon önerisi</h2>
        <Button type="button" size="sm" variant="secondary" onClick={() => setAcik((a) => !a)}>
          {acik ? "Kapat" : "Aç"}
        </Button>
      </div>

      {acik ? (
        <div className="flex flex-col gap-3">
          <AiAdvisoryNotice />

          <FormField label="Kaynak versiyon" required>
            <Select value={versiyon} onChange={(e) => setVersiyon(e.target.value)}>
              <option value="">Seçin</option>
              {metinliVersiyonlar.map((v) => (
                <option key={v.id} value={v.id}>
                  Versiyon {v.versionNumber}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Hedef madde/paragraf" required>
            <Select
              value={hedef}
              disabled={versiyon === "" || belge.isPending}
              onChange={(e) => {
                setHedef(e.target.value);
                setCalisma(null);
              }}
            >
              <option value="">Seçin</option>
              {secenekler.map((h) => (
                <option key={h.deger} value={h.deger}>
                  {h.etiket}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Ne istiyorsunuz?" required>
            <Textarea
              rows={2}
              value={talimat}
              onChange={(e) => setTalimat(e.target.value)}
              placeholder="Örn. Cezai şarta toplam bedelin %10'u üst sınırını ekle."
            />
          </FormField>

          <div className="flex justify-end">
            <Button
              type="button"
              loading={uret.isPending}
              disabled={secili === null || talimat.trim() === "" || uret.isPending}
              onClick={() => uret.mutate()}
            >
              {uret.isPending ? "Üretiliyor…" : calisma ? "Yeniden üret" : "Öneri üret"}
            </Button>
          </div>

          {calisma?.suggestion ? (
            <div className="flex flex-col gap-3" data-testid="ai-revizyon-sonuc">
              {/* ⚠ Mevcut ve önerilen YAN YANA; mobilde alt alta. */}
              <div className="grid gap-3 sm:grid-cols-2">
                <section className="rounded-lg border border-border-subtle p-3">
                  <h3 className="text-sm font-medium text-fg-strong">Mevcut metin</h3>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-fg">{calisma.currentText}</p>
                </section>
                <section className="rounded-lg border border-primary/40 bg-primary-soft/40 p-3">
                  <h3 className="text-sm font-medium text-fg-strong">Önerilen metin</h3>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-fg">
                    {calisma.suggestion.proposedText}
                  </p>
                </section>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
                <Badge tone="neutral">{calisma.model}</Badge>
                <span className="tabular-nums">{calisma.credits} kredi</span>
                {calisma.stale ? <Badge tone="warning">Kaynak metin değişmiş</Badge> : null}
              </div>

              <section>
                <h3 className="text-sm font-medium text-fg-strong">Gerekçe</h3>
                <p className="mt-1 text-sm text-fg">{calisma.suggestion.rationale}</p>
                {calisma.suggestion.purpose ? (
                  <p className="mt-1 text-sm text-fg-muted">
                    AI'ın anladığı amaç: {calisma.suggestion.purpose}
                  </p>
                ) : null}
              </section>

              {calisma.suggestion.assumptions.length > 0 ? (
                <section>
                  <h3 className="text-sm font-medium text-fg-strong">AI'ın yaptığı varsayımlar</h3>
                  <ul className="mt-1 list-disc pl-5 text-sm text-fg-muted">
                    {calisma.suggestion.assumptions.map((v, i) => (
                      <li key={i}>{v}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {/*
                ⚠ SESSİZ OLGU KAYBI UYARISI (CLOS-1085). Bir RED değil: kullanıcı zaten
                tutarı değiştirmek istemiş olabilir. Uyarı olmadan, öneriyi hızlıca okuyan
                bir hukukçu düşen bir tarihi ya da tavanı fark etmeyebilirdi.
              */}
              {calisma.droppedFacts.length > 0 ? (
                <InfoBanner tone="warning">
                  <div data-testid="ai-revizyon-olgu-kaybi">
                    <p className="font-medium">{CONTRACT_AI_REVISION_FACT_LOSS_WARNING}</p>
                    <p className="mt-1 text-sm">Görünmeyenler: {calisma.droppedFacts.join(", ")}</p>
                  </div>
                </InfoBanner>
              ) : null}

              {/* ⚠ EKSİK BAĞLAM AYRI: kullanıcının doldurması gereken boşluk, varsayım değil. */}
              {calisma.suggestion.missingContext.length > 0 ? (
                <InfoBanner tone="warning">
                  <div data-testid="ai-revizyon-eksik">
                    <p className="font-medium">Metinde olmayan bilgiler</p>
                    <ul className="mt-1 list-disc pl-5 text-sm">
                      {calisma.suggestion.missingContext.map((v, i) => (
                        <li key={i}>{v}</li>
                      ))}
                    </ul>
                  </div>
                </InfoBanner>
              ) : null}

              <div className="flex flex-wrap gap-2 border-t border-border-subtle pt-3">
                {calisma.savedRevisionId ? (
                  <Badge tone="success">Revizyon önerisi olarak kaydedildi</Badge>
                ) : (
                  <>
                    {/*
                      ⚠ AYRI ve AÇIK KOMUT: kanonik revizyon ancak burada doğuyor. Kabul/ret
                      ya da metne uygulama BU EKRANDA YOK — onlar kendi komutları.
                    */}
                    <Button
                      type="button"
                      loading={kaydet.isPending}
                      disabled={kaydet.isPending}
                      onClick={() => kaydet.mutate(calisma.id)}
                    >
                      {kaydet.isPending ? "Kaydediliyor…" : "Revizyon önerisi olarak kaydet"}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setCalisma(null)}>
                      Vazgeç
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/** Madde başlıkları ve paragrafları — `RevisionProposalDialog`'daki hedef listesiyle aynı biçim. */
function hedefSecenekleri(
  document: ContractDocument | null,
): { deger: string; etiket: string; metin: string }[] {
  if (!document) return [];
  return document.clauses.flatMap((madde) => [
    {
      deger: `${madde.id}#baslik`,
      etiket: `${madde.title || "(başlıksız madde)"} — başlık`,
      metin: madde.title,
    },
    ...madde.blocks.map((blok, i) => ({
      deger: `${madde.id}#${i}`,
      etiket: `${madde.title || "(başlıksız madde)"} — ${i + 1}. paragraf`,
      metin: blok.text,
    })),
  ]);
}
